// src/pages/CustomerAccount/pages/CustomerAccount.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Search, Menu, X } from 'lucide-react';

import { logout } from '../../../redux/features/Auth/authSlice'; // ✅ اصلاح
import { getMyOrderList } from '../../../redux/features/Order/orderThunk';
import { fetchProducts } from '../../../redux/features/Product/productThunk';

import { COLORS } from './Components/CustomerAccount.styles';
import Sidebar from './Components/Sidebar';
import DashboardContent from './Components/DashboardContent';
import {
  OrdersContent,
  AddressesContent,
  WishlistContent,
  WalletContent,
  MarketingContent,
  SupportContent
} from './Components/TabContents';

function CustomerAccount() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // دریافت اطلاعات از Redux
  const { userInfo: userInfoFromRedux } = useSelector((state) => state.auth) || {}; // ✅ تغییر
  const { orders: ordersFromRedux, loading: loadingOrders } = useSelector((state) => state.orderListMy) || { orders: [], loading: false };
  const { user, loading: loadingUser } = useSelector((state) => state.userDetails) || { user: {} };
  const { products, loading: loadingProducts } = useSelector((state) => state.listProduct) || { products: [] };
  
  const { shippingAddress } = useSelector((state) => state.cart) || {};

  // تبدیل داده‌های Redux به فرمت مورد استفاده در کامپوننت
  const userInfo = {
    firstName: user?.first_name || userInfoFromRedux?.name?.split(' ')[0] || 'کاربر',
    lastName: user?.last_name || userInfoFromRedux?.name?.split(' ').slice(1).join(' ') || '',
    email: userInfoFromRedux?.email || '',
    phone: userInfoFromRedux?.phone || '',
    memberSince: userInfoFromRedux?.date_joined ? new Date(userInfoFromRedux.date_joined).toLocaleDateString('fa-IR') : 'عضو جدید',
    orders: ordersFromRedux?.length || 0,
    address: userInfoFromRedux?.address || 'آدرس ثبت نشده',
    postalCode: userInfoFromRedux?.postalCode || '',
    city: userInfoFromRedux?.city || '',
    province: userInfoFromRedux?.province || '',
    about: userInfoFromRedux?.about || '',
  };

  // مرتب‌سازی و گرفتن ۳ سفارش آخر (جدیدترین ابتدا)
  const recentOrders = (ordersFromRedux || [])
    .slice()
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .slice(0, 3)
    .map(order => ({
      id: `#${order._id}`,
      date: new Date(order.createdAt).toLocaleDateString('fa-IR'),
      total: `${order.totalPrice?.toLocaleString()} تومان`,
      paid: order.isPaid ? 'بله' : 'خیر',
      delivered: order.isDelivered ? 'تحویل شده' : (order.isPaid ? 'در حال ارسال' : 'در انتظار'),
      status: order.isDelivered ? 'delivered' : (order.isPaid ? 'processing' : 'pending'),
      productId: order.orderItems?.[0]?.product,
      _id: order._id,
    }));

  const discountedProducts = (products || [])
    .filter(p => p.discountPercentage && p.discountPercentage > 0)
    .slice(0, 9);

  const [activeTab, setActiveTab] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState(null);
  const [showAddAddress, setShowAddAddress] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // دریافت اولیه داده‌ها
  useEffect(() => {
    if (userInfoFromRedux?.access) {
      dispatch(getMyOrderList());
      dispatch(fetchProducts());
    }
  }, [dispatch, userInfoFromRedux]);

  // به‌روزرسانی سفارشات هنگام فعال شدن تب پیشخوان
  useEffect(() => {
    if (userInfoFromRedux?.access && activeTab === 'dashboard') {
      dispatch(getMyOrderList());
    }
  }, [activeTab, dispatch, userInfoFromRedux]);

  useEffect(() => {
    if (!sidebarOpen) return;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = 'unset'; };
  }, [sidebarOpen]);

  useEffect(() => {
    const onKeyDown = (e) => { if (e.key === 'Escape') setSidebarOpen(false); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const handleEdit = (section) => {
    setIsEditing(true);
    setEditData({ ...userInfo, section });
  };

  const handleSave = (updatedData) => {
    setSuccessMessage('✅ اطلاعات با موفقیت ویرایش شد!');
    setTimeout(() => setSuccessMessage(''), 3000);
    setIsEditing(false);
    setEditData(null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditData(null);
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return (
          <DashboardContent
            userInfo={userInfo}
            recentOrders={recentOrders}
            discountedProducts={discountedProducts}
            loadingProducts={loadingProducts}
            onEdit={handleEdit}
            isEditing={isEditing}
            editData={editData}
            onSave={handleSave}
            onCancel={handleCancel}
            onViewAllOrders={() => setActiveTab('orders')}
            navigate={navigate}
            successMessage={successMessage}
            loadingOrders={loadingOrders}
            loadingUser={loadingUser}
          />
        );
      case 'orders':
        return <OrdersContent navigate={navigate} />;
      case 'addresses':
        return (
          <AddressesContent
            userInfo={userInfo}
            shippingAddress={shippingAddress}
            onEdit={handleEdit}
            isEditing={isEditing}
            editData={editData}
            onSave={handleSave}
            onCancel={handleCancel}
            showAddAddress={showAddAddress}
            setShowAddAddress={setShowAddAddress}
          />
        );
      case 'wishlist':
        return <WishlistContent />;
      case 'wallet':
        return <WalletContent />;
      case 'marketing':
        return <MarketingContent />;
      case 'support':
        return <SupportContent />;
      default:
        return (
          <DashboardContent
            userInfo={userInfo}
            recentOrders={recentOrders}
            discountedProducts={discountedProducts}
            loadingProducts={loadingProducts}
            onEdit={handleEdit}
            isEditing={isEditing}
            editData={editData}
            onSave={handleSave}
            onCancel={handleCancel}
            onViewAllOrders={() => setActiveTab('orders')}
            navigate={navigate}
            successMessage={successMessage}
            loadingOrders={loadingOrders}
            loadingUser={loadingUser}
          />
        );
    }
  };

  return (
    <div dir="rtl" className="min-h-screen overflow-x-hidden" style={{ backgroundColor: COLORS.primaryBg }}>
      <header className="sticky top-0 z-30 border-b bg-white/95 shadow-sm backdrop-blur-md" style={{ borderColor: COLORS.border }}>
        <div className="mx-auto max-w-7xl px-3 sm:px-5 lg:px-8">
          <div className="flex h-14 items-center justify-between gap-2 sm:h-16 sm:gap-3">
            <div className="flex min-w-0 items-center gap-1.5 sm:gap-3">
              <button onClick={() => setSidebarOpen(v => !v)} className="inline-flex h-10 w-10 items-center justify-center rounded-xl transition-colors hover:bg-[#f0f3ec] active:scale-95 lg:hidden">
                {sidebarOpen ? <X className="h-5 w-5" style={{ color: COLORS.text }} /> : <Menu className="h-5 w-5" style={{ color: COLORS.text }} />}
              </button>
              <div className="flex min-w-0 shrink-0 items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-xl sm:h-9 sm:w-9" style={{ backgroundColor: COLORS.primary }}>
                  <span className="text-sm font-bold text-white">A</span>
                </div>
                <span className="truncate text-base font-bold sm:text-xl" style={{ color: COLORS.primary }}>arion</span>
              </div>
            </div>
            <div className="mx-2 hidden min-w-0 max-w-md flex-1 md:block lg:max-w-lg">
              <div className="relative">
                <Search className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2" style={{ color: COLORS.primaryLight }} />
                <input type="search" placeholder="جستجو در محصولات و سفارش‌ها..." className="w-full rounded-xl border bg-white/60 py-2.5 pl-4 pr-10 text-sm outline-none transition placeholder:opacity-70 focus:ring-2" style={{ borderColor: COLORS.border, color: COLORS.text, '--tw-ring-color': `${COLORS.primary}55` }} />
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2 border-r pr-2 sm:gap-3 sm:pr-3" style={{ borderColor: COLORS.border }}>
              <div className="hidden min-w-0 text-right sm:block">
                <p className="max-w-[110px] truncate text-sm font-semibold md:max-w-[140px] lg:max-w-[180px]" style={{ color: COLORS.text }}>
                  {userInfo.firstName} {userInfo.lastName}
                </p>
                <p className="max-w-[120px] truncate text-xs md:max-w-[150px] lg:max-w-[200px]" style={{ color: COLORS.primaryLight }}>
                  {userInfo.email}
                </p>
              </div>
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-bold text-white shadow-md sm:h-10 sm:w-10" style={{ background: `linear-gradient(135deg, ${COLORS.primary}, ${COLORS.primaryDark})` }}>
                {userInfo.firstName?.[0] || 'U'}
              </div>
            </div>
          </div>
          <div className="pb-3 md:hidden">
            <div className="relative">
              <Search className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2" style={{ color: COLORS.primaryLight }} />
              <input type="search" placeholder="جستجو..." className="w-full rounded-xl border bg-white/70 py-2.5 pl-3 pr-10 text-sm outline-none transition placeholder:opacity-70 focus:ring-2" style={{ borderColor: COLORS.border, color: COLORS.text, '--tw-ring-color': `${COLORS.primary}55` }} />
            </div>
          </div>
        </div>
      </header>

      {sidebarOpen && <button onClick={() => setSidebarOpen(false)} className="fixed inset-0 z-40 bg-black/40 backdrop-blur-[2px] lg:hidden" />}

      <div className="mx-auto max-w-7xl px-3 py-3 sm:px-5 sm:py-5 lg:px-8 lg:py-6">
        <div className="flex items-start gap-0 lg:gap-6">
          <Sidebar
            userInfo={userInfo}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
          />
          <main className="min-w-0 w-full flex-1">
            <div className="rounded-xl border bg-white/80 p-3 shadow-lg backdrop-blur-sm sm:rounded-2xl sm:p-5 lg:p-6" style={{ boxShadow: `0 8px 32px ${COLORS.primary}15`, borderColor: COLORS.border }}>
              {renderContent()}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}

export default CustomerAccount;